import { useEffect, useState } from 'react';
import { api, errorText } from '../lib/api';
import { classNames, fmtDateTime, useServerEvents } from '../lib/util';

export const DOC_KIND_LABEL: Record<string, string> = {
  rulebook: 'Rulebook',
  house_rules: 'House rules',
  handout: 'Handout',
  notes: 'Session notes',
  chat: 'Chat log',
  image: 'Image / screenshot',
  handwriting: 'Handwritten notes',
  other: 'Other',
};

const STATUS_LABEL: Record<string, string> = { uploaded: 'Waiting', extracting: 'Extracting', needs_review: 'Needs review', ready: 'Ready', failed: 'Failed' };

export function Documents({ campaignId }: { campaignId: string }) {
  const [docs, setDocs] = useState<any[] | null>(null);
  const [campaign, setCampaign] = useState<any>(null);
  const [err, setErr] = useState('');
  const load = async () => {
    try {
      setDocs(await api(`/api/campaigns/${campaignId}/documents`));
    } catch (e) {
      setErr(errorText(e));
    }
  };
  useEffect(() => {
    void load();
    api(`/api/campaigns/${campaignId}/settings`)
      .then((s: any) => setCampaign(s.campaign))
      .catch(() => undefined);
  }, [campaignId]);
  useServerEvents(campaignId, (w) => (w === 'documents' || w === 'jobs') && void load());
  useServerEvents(null, (w, d) => w === 'documents' && !d.campaignId && void load());
  // Poll while anything is extracting (progress updates).
  useEffect(() => {
    if (!docs?.some((d) => d.status === 'extracting' || d.status === 'uploaded')) return;
    const t = window.setInterval(() => void load(), 2000);
    return () => clearInterval(t);
  }, [docs]);
  return (
    <div className="page">
      <h1>Documents</h1>
      <p className="muted">Rulebooks, handouts, chat logs, screenshots and scanned notes. Originals are kept; extracted text can be reviewed and corrected. Embedded macros and scripts are never run.</p>
      <div className="two-col">
        <Upload campaignId={campaignId} campaign={campaign} onDone={load} />
        <Paste campaignId={campaignId} onDone={load} />
      </div>
      <OtherCampaignDocs campaignId={campaignId} assigned={docs ?? []} onDone={load} />
      {err && <p className="error">{err}</p>}
      {docs === null ? (
        <p className="muted">Loading…</p>
      ) : docs.length === 0 ? (
        <p className="muted">No documents yet.</p>
      ) : (
        <ul className="doc-list">
          {docs.map((d) => (
            <li key={d.id} className="card doc-row">
              <a href={`#/c/${campaignId}/documents/${d.id}`} className="doc-title">
                {d.title}
              </a>
              <span className="muted small">
                {DOC_KIND_LABEL[d.kind]}
                {d.game_system ? ` · ${d.game_system} ${d.edition}` : ''} · {d.page_count ?? '?'} page{d.page_count === 1 ? '' : 's'} · {fmtDateTime(d.created_at)}
                {d.also_in ? ` · also in ${d.also_in}` : ''}
              </span>
              {d.visibility === 'gm_only' && <span className="tag secret">GM only</span>}
              <span className={classNames('st', `doc-${d.status}`)}>{STATUS_LABEL[d.status]}</span>
              {(d.status === 'extracting' || d.status === 'uploaded') && (
                <div className="progress" role="progressbar" aria-valuenow={Math.round(d.progress * 100)} aria-valuemin={0} aria-valuemax={100} aria-label={`Extracting ${d.title}`}>
                  <div style={{ width: `${Math.round(d.progress * 100)}%` }} />
                </div>
              )}
              {d.progress_text && <span className="muted small">{d.progress_text}</span>}
              {d.review_pages > 0 && <span className="warn small">{d.review_pages} page(s) need review</span>}
              {d.error && <span className="error small">{d.error}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Upload({ campaignId, campaign, onDone }: { campaignId: string; campaign: any; onDone: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [f, setF] = useState({ kind: 'handout', title: '', gameSystem: '', edition: '', visibility: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => {
    if (campaign) setF((x) => ({ ...x, gameSystem: x.gameSystem || campaign.game_system, edition: x.edition || campaign.edition }));
  }, [campaign]);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) return;
    const form = new FormData();
    for (const [k, v] of Object.entries(f)) if (v) form.append(k, v);
    form.append('file', file);
    setBusy(true);
    setErr('');
    try {
      await api(`/api/campaigns/${campaignId}/documents`, { form });
      setFile(null);
      (e.target as HTMLFormElement).reset();
      onDone();
    } catch (x) {
      setErr(errorText(x));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="card form-grid" onSubmit={submit}>
      <h2 className="span2">Upload a file</h2>
      <label className="span2">
        File (PDF, DOCX, TXT, MD, PNG, JPEG, WebP)
        <input type="file" accept=".pdf,.docx,.txt,.md,.markdown,.png,.jpg,.jpeg,.webp,image/*" capture={undefined} onChange={(e) => setFile(e.target.files?.[0] ?? null)} required />
      </label>
      <label>
        Kind
        <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}>
          {Object.entries(DOC_KIND_LABEL).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
      </label>
      <label>
        Title
        <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="defaults to file name" />
      </label>
      {(f.kind === 'rulebook' || f.kind === 'house_rules') && (
        <>
          <label>
            Game system
            <input value={f.gameSystem} onChange={(e) => setF({ ...f, gameSystem: e.target.value })} />
          </label>
          <label>
            Edition
            <input value={f.edition} onChange={(e) => setF({ ...f, edition: e.target.value })} />
          </label>
        </>
      )}
      <label>
        Visibility
        <select value={f.visibility} onChange={(e) => setF({ ...f, visibility: e.target.value })}>
          <option value="">Campaign default</option>
          <option value="public">Public</option>
          <option value="gm_only">GM only</option>
        </select>
      </label>
      {err && <p className="error span2">{err}</p>}
      <div className="span2">
        <button className="btn primary" disabled={busy || !file}>
          {busy ? 'Uploading…' : 'Upload'}
        </button>
      </div>
    </form>
  );
}

function Paste({ campaignId, onDone }: { campaignId: string; onDone: () => void }) {
  const [text, setText] = useState('');
  const [mode, setMode] = useState<'entries' | 'document'>('entries');
  const [split, setSplit] = useState<'paragraphs' | 'lines'>('lines');
  const [title, setTitle] = useState('');
  const [sessions, setSessions] = useState<any[]>([]);
  const [sessionId, setSessionId] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  useEffect(() => {
    api(`/api/campaigns/${campaignId}/dashboard`)
      .then((d: any) => {
        setSessions(d.sessions);
        setSessionId(d.sessions[0]?.id ?? '');
      })
      .catch(() => undefined);
  }, [campaignId]);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr('');
    setMsg('');
    try {
      if (mode === 'entries') {
        const r = await api(`/api/campaigns/${campaignId}/import-notes`, { body: { text, split, sessionId } });
        setMsg(`Imported ${r.imported} note(s) into the session; AI will organize them in the background.`);
      } else {
        await api(`/api/campaigns/${campaignId}/documents/text`, { body: { text, title: title || 'Pasted text', kind: 'chat' } });
        setMsg('Saved as a reference document.');
      }
      setText('');
      onDone();
    } catch (x) {
      setErr(errorText(x));
    }
  };
  return (
    <form className="card form-grid" onSubmit={submit}>
      <h2 className="span2">Paste chat messages or notes</h2>
      <div className="span2 row wrap">
        <label className="check">
          <input type="radio" checked={mode === 'entries'} onChange={() => setMode('entries')} /> Import as session notes
        </label>
        <label className="check">
          <input type="radio" checked={mode === 'document'} onChange={() => setMode('document')} /> Keep as reference document
        </label>
      </div>
      {mode === 'entries' ? (
        <>
          <label>
            Session
            <select value={sessionId} onChange={(e) => setSessionId(e.target.value)} required>
              {sessions.map((s) => (
                <option key={s.id} value={s.id}>
                  Session {s.number}
                  {s.title ? ` — ${s.title}` : ''}
                </option>
              ))}
            </select>
          </label>
          <label>
            One note per
            <select value={split} onChange={(e) => setSplit(e.target.value as any)}>
              <option value="lines">line (chat messages)</option>
              <option value="paragraphs">paragraph</option>
            </select>
          </label>
        </>
      ) : (
        <label className="span2">
          Title
          <input value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>
      )}
      <label className="span2">
        Text
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={6} required />
      </label>
      {msg && <p className="ok span2">{msg}</p>}
      {err && <p className="error span2">{err}</p>}
      <div className="span2">
        <button className="btn primary" disabled={!text.trim() || (mode === 'entries' && !sessionId)}>
          Import
        </button>
      </div>
    </form>
  );
}

/** Reusing a document from another campaign requires explicit assignment. */
function OtherCampaignDocs({ campaignId, assigned, onDone }: { campaignId: string; assigned: any[]; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [all, setAll] = useState<any[]>([]);
  useEffect(() => {
    if (open) api<any[]>('/api/documents').then(setAll).catch(() => undefined);
  }, [open]);
  const others = all.filter((d) => !assigned.some((a) => a.id === d.id));
  return (
    <details className="card" open={open} onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}>
      <summary>Use a document from another campaign</summary>
      {others.length === 0 ? (
        <p className="muted">No other documents.</p>
      ) : (
        <ul className="plain">
          {others.map((d) => (
            <li key={d.id} className="row">
              <span className="grow">
                {d.title} <span className="muted small">({DOC_KIND_LABEL[d.kind]}{d.game_system ? `, ${d.game_system} ${d.edition}` : ''})</span>
              </span>
              <button
                className="btn small"
                onClick={async () => {
                  await api(`/api/documents/${d.id}/assign`, { body: { campaignId, assign: true } });
                  onDone();
                  setAll(await api('/api/documents'));
                }}
              >
                Assign to this campaign
              </button>
            </li>
          ))}
        </ul>
      )}
    </details>
  );
}
